import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import {
  ChevronRight,
  Flame, Award, ShoppingBag, MessageCircle, Bell, Heart, Settings, Snowflake,
  RefreshCw,
} from 'lucide-react'
import { Layout } from '../components/Layout'
import { GlassCard } from '../components/GlassCard'
import { ProgressBar } from '../components/ProgressBar'
import { MyLeagueWidget } from '../components/MyLeagueWidget'
import { TestWeekBanner } from '../components/TestWeekBanner'
import { formatStartDate } from '../components/Layout'
import { FreezeActiveModal } from '../components/modals/FreezeActiveModal'
import { LifeLostModal } from '../components/modals/LifeLostModal'
import { LastChanceModal } from '../components/modals/LastChanceModal'
import { WeeklyResultsModal } from '../components/modals/WeeklyResultsModal'
import { LevelCapModal } from '../components/modals/LevelCapModal'
import { TuCoinInfoSheet } from '../components/modals/TuCoinInfoSheet'
import { StreakHistorySheet } from '../components/modals/StreakHistorySheet'
import { LevelsProgressionSheet } from '../components/modals/LevelsProgressionSheet'
import { LivesInfoSheet } from '../components/modals/LivesInfoSheet'
import { FreezeInfoSheet } from '../components/modals/FreezeInfoSheet'
import { TuCoinIcon } from '../components/TuCoinIcon'
// import { TfaLogo } from '../components/TfaLogo' // больше не используется напрямую — заменено на <TuCoinIcon />
import { FatSecretConnectSheet } from '../components/modals/FatSecretConnectSheet'
import { NotificationsPanel, NOTIFICATIONS_QUERY_KEY } from '../components/NotificationsPanel'
import { useAuth } from '../hooks/useAuth'
import { useCuratorHidden } from '../hooks/useCuratorHidden'
import { useFeatureAccess } from '../hooks/useFeatureAccess'
import { lockText, LOCK_CTA, SALES_URL } from '../utils/featureLocks'
import { useTelegram } from '../hooks/useTelegram'
import { fatSecretStatusRefetchInterval } from '../lib/fatsecretPolling'
import { api, resolveMediaUrl } from '../services/api'
import { LEVEL_XP_REQUIREMENTS, effectiveCharacterLevel, getXpForNextLevel, isLevelCapped, latestWeightEma, pickDailyTip, weeklyWeightDeltaPct, weightTrendTone } from '@tfa/shared'
import personaImg from '../assets/persona.png'
import gymBgImg from '../assets/home-gym-bg.jpg'
import { getCharacterImage } from '../assets/character'
import { FatSecretIcon } from '../components/FatSecretIcon'

const F = 'Roboto, sans-serif'

export function Home() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { impactHaptic, openLink, notificationHaptic, showAlert } = useTelegram()

  // FatSecret status + connect/sync — single bright CTA in demo mode (per prototype Dashboard).
  const queryClient = useQueryClient()
  const { data: fatSecretStatus } = useQuery({
    queryKey: ['fatsecret-status'],
    queryFn: () => api.getFatSecretStatus(),
    // Опрос — только пока клиент подключает FatSecret (см. lib/fatsecretPolling).
    refetchInterval: (q) => fatSecretStatusRefetchInterval(q.state.data?.connected),
    refetchOnWindowFocus: true,
  })
  const fatSecretConnected = !!fatSecretStatus?.connected
  const [fsSheet, setFsSheet] = useState<{ open: boolean; mode: 'connect' | 'reconnect' }>({ open: false, mode: 'connect' })
  const [showNotifs, setShowNotifs] = useState(false)
  const connectFatSecretMutation = useMutation({
    mutationFn: () => api.connectFatSecret(),
    onSuccess: (data: { authUrl: string }) => openLink(data.authUrl, { external: true }),
    onError: () => { notificationHaptic('error'); showAlert('Не удалось подключить FatSecret') },
  })
  const syncFatSecretMutation = useMutation({
    mutationFn: () => api.syncFatSecret(),
    onSuccess: () => {
      notificationHaptic('success')
      // Refresh fatsecret status (lastSyncAt) + dashboard + diary so the
      // synced meals appear immediately without a manual reload.
      queryClient.invalidateQueries({ queryKey: ['fatsecret-status'] })
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: string } } } | null)?.response?.data?.error
      // 401 FATSECRET_TOKEN_EXPIRED → connection was auto-deleted server-side;
      // refresh status and open the reconnect sheet (replaces the alert).
      if (code === 'FATSECRET_TOKEN_EXPIRED') {
        notificationHaptic('warning')
        queryClient.invalidateQueries({ queryKey: ['fatsecret-status'] })
        setFsSheet({ open: true, mode: 'reconnect' })
        return
      }
      notificationHaptic('error')
      showAlert('Не удалось обновить дневник FatSecret')
    },
  })

  // Tooltip "Ожидай старт" — shown when user taps any dimmed/blocked element in demo (R2 / doc 17).
  const [peekTip, setPeekTip] = useState<{ x: number; y: number; key: number } | null>(null)
  const peekTipTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handlePeekTap: React.MouseEventHandler = (e) => {
    if (peekTipTimer.current) clearTimeout(peekTipTimer.current)
    setPeekTip({ x: e.clientX, y: e.clientY, key: Date.now() })
    peekTipTimer.current = setTimeout(() => setPeekTip(null), 1500)
  }
  useEffect(() => () => {
    if (peekTipTimer.current) clearTimeout(peekTipTimer.current)
  }, [])

  const { data: dashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.getDashboard(),
  })

  const { data: streakStatus } = useQuery({
    queryKey: ['streak', 'status'],
    queryFn: () => api.getStreakStatus(),
  })

  const { data: leaderboard } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => api.getLeaderboard(),
  })

  // Вес «с поправкой» (EMA) должен совпадать с дневником и подробной аналитикой
  // (bug #137). Для этого EMA считается по той же полной 365-дневной истории,
  // что и на вкладке «Вес» аналитики — тот же queryKey переиспользует кэш.
  const weightHistEnd = new Date().toISOString().slice(0, 10)
  const weightHistStart = new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10)
  const { data: weightHistory } = useQuery({
    queryKey: ['analytics', 'weight', weightHistStart, weightHistEnd],
    queryFn: () => api.getDiaryEntries({ startDate: weightHistStart, endDate: weightHistEnd }).catch(() => ({ entries: [] })),
    staleTime: 60_000,
  })

  // Analytics carousel: last 14 days of diary entries + all measurements
  const { data: analyticsHistory } = useQuery({
    queryKey: ['home', 'analytics'],
    queryFn: async () => {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() - 13)
      const fmt = (d: Date) => d.toISOString().slice(0, 10)
      const [diary, workouts] = await Promise.all([
        api.getDiaryEntries({ startDate: fmt(start), endDate: fmt(today) }).catch(() => ({ entries: [] })),
        api.getWorkoutEntries(fmt(start), fmt(today)).catch(() => ({ entries: [] })),
      ])
      return {
        entries: ((diary as any)?.entries ?? []) as Array<{ date: string; sleep: number | null; sleepQuality: 'GOOD' | 'SO_SO' | 'BAD' | null; steps: number | null; weight: number | null; training: string | null }>,
        workouts: ((workouts as any)?.entries ?? []) as Array<{ date: string }>,
      }
    },
    staleTime: 5 * 60_000,
  })

  // ── Gamification modals (doc: prototype event logic) ──
  const [showFreezeModal, setShowFreezeModal] = useState(false)
  const [showLifeLostModal, setShowLifeLostModal] = useState(false)
  const [showLastChanceModal, setShowLastChanceModal] = useState(false)
  const [showWeeklyResultsModal, setShowWeeklyResultsModal] = useState(false)
  const [showLevelCapModal, setShowLevelCapModal] = useState(false)
  // Обратная связь («ОС») — Telegram-чат поддержки, заменил виджет ИИ-тренера (bug #130)
  const FEEDBACK_URL = 'https://t.me/m/eZN1bd6jNWI1'
  const [showTuCoinSheet, setShowTuCoinSheet] = useState(false)
  const [showStreakHistory, setShowStreakHistory] = useState(false)
  const [showLevelsSheet, setShowLevelsSheet] = useState(false)
  const [showLivesSheet, setShowLivesSheet] = useState(false)
  const [showFreezeSheet, setShowFreezeSheet] = useState(false)

  useEffect(() => {
    if (!streakStatus) return

    // Auto-dismiss: если состояние восстановилось — снимаем уже открытые модалки.
    // Например, после Last Chance клиент выполнил действие → lives > 0 → модалка должна уйти.
    // Также сбрасываем sessionStorage-флаг, чтобы при следующем падении lives=0 модалка снова сработала ОДИН раз.
    if (streakStatus.lives > 0) {
      if (showLastChanceModal) setShowLastChanceModal(false)
      try { sessionStorage.removeItem('lastChanceShown') } catch { /* ignore */ }
    }
    if (!streakStatus.needsAction) {
      if (showLifeLostModal) setShowLifeLostModal(false)
      try { sessionStorage.removeItem('lifeLostShown') } catch { /* ignore */ }
    }

    // Priority: last chance > life lost > level cap.
    // Заморозкой управляет глобальный FreezeStatusChip (Layout).
    // Флаги хранятся в sessionStorage, чтобы переход между экранами / SSE-refetch
    // не открывали модалку повторно после ручного закрытия.
    const lastChanceShown = sessionStorage.getItem('lastChanceShown') === '1'
    const lifeLostShown = sessionStorage.getItem('lifeLostShown') === '1'

    if (streakStatus.lives === 0 && !lastChanceShown) {
      sessionStorage.setItem('lastChanceShown', '1')
      setShowLastChanceModal(true)
    } else if (streakStatus.lives < 3 && streakStatus.needsAction && !lifeLostShown) {
      sessionStorage.setItem('lifeLostShown', '1')
      setShowLifeLostModal(true)
    } else if (user && isLevelCapped(user.level || 1, user.product) && !sessionStorage.getItem('levelCapShown')) {
      sessionStorage.setItem('levelCapShown', '1')
      setShowLevelCapModal(true)
    }
  }, [streakStatus, user, showLastChanceModal, showLifeLostModal])

  if (!user) return null

  // Demo / test-week visual modes (doc 17 + prototype DemoOverlay).
  // Demo: status=AWAITING_QUESTIONNAIRE / AWAITING_CURATOR / AWAITING_START — degrade content,
  //   values shown as "—", carousels dimmed + grayscaled, FatSecret-style CTA highlighted.
  // Test week: full UI + expandable orange banner above streak (day X / 7) + 🧪 chip in header.
  const status = (user as any)?.status as string | undefined
  const mode = (user as any)?.mode as string | undefined
  const isDemo = status === 'AWAITING_QUESTIONNAIRE' || status === 'AWAITING_CURATOR' || status === 'AWAITING_START'
  const isTestWeek = mode === 'TEST_WEEK'

  // Notifications unread count — drives the red dot on the bell. Shares cache
  // (same query key) with NotificationsPanel, so opening the panel uses already-
  // fetched data, and marking-all-read invalidates the cache → dot disappears.
  const { data: notificationsData } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => api.getNotifications({ limit: 50 }),
    enabled: !isDemo,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const unreadNotifsCount = notificationsData?.unreadCount ?? 0

  // Test-week day (1–7): flowStartDate is reset to "now" by the API every
  // time the client transitions to TEST_WEEK (auto-scheduler or admin), so
  // День = floor((now − flowStartDate)/24h) + 1, clamped to [1, 7]. Time-shift
  // buttons in Profile decrement flowStartDate, advancing the displayed day.
  const flowStartDate = (user as any)?.flowStartDate
    ? new Date((user as any).flowStartDate as string)
    : null
  const testWeekDay = isTestWeek && flowStartDate
    ? Math.max(1, Math.min(7, Math.floor((Date.now() - flowStartDate.getTime()) / 86_400_000) + 1))
    : 1

  const currentLevel = user.level || 1
  // #355: внешность персонажа может быть поднята админом по запросу клиента
  // (маскот первых уровней у части людей вызывает стресс, а не мотивацию).
  // Меняется ТОЛЬКО картинка: уровень, XP и прогресс ниже — настоящие, а выше
  // порога прогрессия продолжается как у всех.
  const characterLevel = effectiveCharacterLevel(
    currentLevel,
    (user as { characterMinLevel?: number | null })?.characterMinLevel,
  )
  const currentXp = user.xp || 0
  const xpForCurrentLevel = LEVEL_XP_REQUIREMENTS[currentLevel as keyof typeof LEVEL_XP_REQUIREMENTS] || 0
  const xpForNextLevel = getXpForNextLevel(currentLevel)
  const xpProgress = currentXp - xpForCurrentLevel
  const xpNeeded = xpForNextLevel - xpForCurrentLevel
  const xpPercent = xpNeeded > 0 ? Math.round((xpProgress / xpNeeded) * 100) : 0

  const initials = (user.firstName || 'A').charAt(0).toUpperCase() + (user.lastName || '').charAt(0).toUpperCase()
  const streakDays = dashboard?.streak ?? user.streakDays ?? 0
  const tucoin = dashboard?.tucoin ?? (user as any).tucoin ?? 0
  const streakRecord = (user as any).maxStreakDays ?? streakDays
  const lives = dashboard?.lives ?? streakStatus?.lives ?? 3

  // #302: «подсказка дня» под персонажем — короткий принцип о питании,
  // тренировках или отношении к весам, как совет при загрузке уровня в играх.
  // День берём по устройству (та же граница суток, что у серии), человека — по
  // id, чтобы у двоих рядом в один день были разные советы.
  const dailyTip = pickDailyTip(new Date().toLocaleDateString('sv-SE'), user.id ?? '')

  // Today's numeric metrics for character card (returned by /users/dashboard)
  // todayValues (sleep/steps/weight) больше не показываются на главной — чипы перенесены/убраны.
  // Логика стрика на главной свёрнута в чип под персонажем; недельная мини-сетка показывается в StreakHistorySheet.

  // Analytics carousel
  const [analyticsIdx, setAnalyticsIdx] = useState(0)
  const analyticsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const id = setInterval(() => {
      setAnalyticsIdx(prev => {
        const next = (prev + 1) % 4
        analyticsRef.current?.scrollTo({ left: next * (analyticsRef.current.offsetWidth), behavior: 'smooth' })
        return next
      })
    }, 6000)
    return () => clearInterval(id)
  }, [])

  // Active challenge from dashboard
  const activeChallenge = dashboard?.activeChallenge
  const challengeProgress = activeChallenge
    ? Math.round((activeChallenge.daysCompleted / activeChallenge.durationDays) * 100)
    : 0

  // Curator (name, telegramUsername, avatarUrl) — display-only on Home.
  // Связь с куратором происходит через чат с ботом в Telegram (после закрытия
  // мини-приложения), поэтому здесь нет кнопок-deep-link на t.me.
  const curator = dashboard?.curator

  const isClub = user?.product === 'CLUB'
  // #534: в Клубе куратора нет — блок связи с ним с главной убран целиком.
  const curatorHidden = useCuratorHidden()
  // #545 (три тарифа): персонаж «серым» — по матрице тарифа.
  const features = useFeatureAccess()
  const characterGray = features.state('character') === 'gray'
  const gamificationOn = features.has('gamification')

  // Leaderboard data
  const myLeague = leaderboard as any

  // ── Analytics carousel data (last 14 days) ──
  const last7DaysISO = (() => {
    const arr: string[] = []
    const today = new Date()
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      arr.push(d.toISOString().slice(0, 10))
    }
    return arr
  })()
  const entriesByDate = new Map<string, { sleep: number | null; sleepQuality: 'GOOD' | 'SO_SO' | 'BAD' | null; steps: number | null; weight: number | null; training: string | null }>()
  for (const e of analyticsHistory?.entries ?? []) {
    const key = new Date(e.date).toISOString().slice(0, 10)
    entriesByDate.set(key, e)
  }
  const workoutDates = new Set<string>()
  for (const w of analyticsHistory?.workouts ?? []) {
    workoutDates.add(new Date(w.date).toISOString().slice(0, 10))
  }
  const stepsSeries = last7DaysISO.map(d => entriesByDate.get(d)?.steps ?? null)
  // Сон теперь оценивается качеством (GOOD/SO_SO/BAD), а не часами. Для совместимости
  // со старыми записями числовой sleep маппим в качество по тем же порогам, что в аналитике.
  type SleepQ = 'GOOD' | 'SO_SO' | 'BAD'
  const sleepQSeries: (SleepQ | null)[] = last7DaysISO.map(d => {
    const e = entriesByDate.get(d)
    if (e?.sleepQuality === 'GOOD' || e?.sleepQuality === 'SO_SO' || e?.sleepQuality === 'BAD') return e.sleepQuality
    if (typeof e?.sleep === 'number') return e.sleep >= 7 ? 'GOOD' : e.sleep >= 5.5 ? 'SO_SO' : 'BAD'
    return null
  })
  // Weight series: daily diary entries (DiaryEntry.weight) — same source as the
  // "Вес" indicator on the diary page. Measurements no longer carry weight.
  const weightPoints: { date: string; weight: number }[] = []
  for (const e of analyticsHistory?.entries ?? []) {
    if (e.weight != null) weightPoints.push({ date: new Date(e.date).toISOString().slice(0, 10), weight: e.weight })
  }
  weightPoints.sort((a, b) => a.date.localeCompare(b.date))
  const weightLast = weightPoints.length > 0 ? weightPoints[weightPoints.length - 1].weight : null
  // weightFirst/weightDelta больше не показываются на главной — убраны вместе с бейджем «+N кг».

  // Недельный темп веса для карточки «Прогресс · Вес» — НЕДЕЛЯ-К-НЕДЕЛЕ по средним
  // (единый расчёт из @tfa/shared). Считаем по полной истории (а не по 14-дневному
  // окну карусели), чтобы корректно отработало правило «<14 дней — не показываем».
  // Бейдж показываем только при статусе ok; иначе скрываем.
  const weeklyWeightResult = weeklyWeightDeltaPct(
    (weightHistory?.entries ?? []) as Array<{ date: string; weight: number | null }>,
  )
  const weightWeeklyPct: number | null = weeklyWeightResult.status === 'ok' ? weeklyWeightResult.pct : null
  // #562: тон дельты по цели клиента, а не «снижение = хорошо» для всех.
  const weightWeeklyTone = weightTrendTone(weightWeeklyPct, (user as { goal?: string | null } | undefined)?.goal ?? null)

  // Вес с поправкой (EMA, α=0.2) — единый расчёт из @tfa/shared по полной
  // 365-дневной истории, чтобы число совпадало с дневником и аналитикой (bug #137).
  const weightSmoothLast: number | null = latestWeightEma(
    (weightHistory?.entries ?? []) as Array<{ date: string; weight: number | null }>,
  )
  const stepsTotal = stepsSeries.reduce<number>((a, b) => a + (b ?? 0), 0)
  const stepsAvg = stepsSeries.filter(s => s != null).length > 0 ? Math.round(stepsTotal / stepsSeries.filter(s => s != null).length) : 0
  const sleepLogged = sleepQSeries.filter(q => q != null).length
  const sleepGoodN = sleepQSeries.filter(q => q === 'GOOD').length
  const sleepGoodPct = sleepLogged > 0 ? Math.round((sleepGoodN / sleepLogged) * 100) : 0
  const SLEEP_Q_COLOR: Record<SleepQ, string> = { GOOD: '#34C759', SO_SO: '#FFCC00', BAD: '#FF3B30' }
  // Workouts heatmap: last 14 days based on training boolean (or null)
  const last14DaysISO = (() => {
    const arr: string[] = []
    const today = new Date()
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      arr.push(d.toISOString().slice(0, 10))
    }
    return arr
  })()
  // A day counts as a workout day if there's a WorkoutDiaryEntry for it OR
  // the user noted training in the daily diary (DiaryEntry.training is a free-text note).
  const workoutsHeatmap = last14DaysISO.map(d => workoutDates.has(d) || entriesByDate.get(d)?.training != null)
  const workoutsCount = workoutsHeatmap.filter(Boolean).length

  return (
    <Layout>
      <div style={{ padding: '14px 16px 24px', fontFamily: F, position: 'relative' }}>

        {/* Полноэкранный фон зала за шапкой и персонажем (расширен до краёв
            экрана и под шапку). Уходит за края через отрицательные отступы,
            сверху лёгкий scrim для читаемости шапки, снизу — плавное затухание
            в цвет страницы, чтобы не было грубой линии-стыка при скролле. */}
        <div aria-hidden style={{
          position: 'absolute', top: -14, left: -16, right: -16,
          height: 'min(84vh, 740px)', zIndex: 0, pointerEvents: 'none',
          background: `url(${gymBgImg}) center 32% / cover`,
          // Плавный переход: верх мягко растворяется в фон страницы (без жёсткого
          // края/потолка), низ так же затухает — стык при скролле не виден.
          WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 15%, #000 73%, transparent 100%)',
          maskImage: 'linear-gradient(180deg, transparent 0%, #000 15%, #000 73%, transparent 100%)',
        }}>
          {/* лёгкая подсветка под шапкой для читаемости чипов */}
          <div style={{ position: 'absolute', inset: 0, background:
            'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.0) 26%)' }} />
        </div>

        {/* ── 1. Header row ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, position: 'relative', zIndex: 1 }}>
          <button
            data-testid="home-profile-avatar"
            onClick={() => { if (!isDemo) { impactHaptic('light'); navigate('/profile') } }}
            disabled={isDemo}
            aria-label="Профиль"
            style={{
              width: 44, height: 44, borderRadius: '50%', padding: 0,
              background: isDemo ? 'rgba(26,58,92,0.09)' : (isClub ? 'linear-gradient(135deg,#00B894,#55EFC4)' : 'linear-gradient(135deg,#6C5CE7,#A29BFE)'),
              border: isDemo ? '2px dashed rgba(26,58,92,0.18)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isDemo ? 18 : 13, fontWeight: 700,
              color: isDemo ? '#C4CDD6' : 'white', flexShrink: 0,
              boxShadow: isDemo ? 'none' : (isClub ? '0 4px 14px rgba(0,184,148,0.35)' : '0 4px 14px rgba(108,92,231,0.35)'),
              cursor: isDemo ? 'default' : 'pointer',
              overflow: 'hidden',
              fontFamily: F,
            }}
          >
            {isDemo ? '?' : (user.telegramAvatar ? (
              <img
                src={resolveMediaUrl(user.telegramAvatar)}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : initials)}
          </button>
          <div
            data-testid="home-character-card"
            data-demo={isDemo ? 'true' : 'false'}
            onClick={() => { if (!isDemo) { impactHaptic('light'); navigate('/profile') } }}
            style={{ flex: 1, minWidth: 0, cursor: isDemo ? 'default' : 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span data-testid="home-character-name" style={{ fontSize: 17, fontWeight: 700, color: '#1C2D41', lineHeight: 1.2 }}>
                {isDemo ? '— —' : (user.firstName || 'Атлет')}
              </span>
              {isTestWeek && !isDemo && (
                <span data-testid="home-test-week-chip" style={{
                  fontSize: 9, fontWeight: 800, color: '#FF9500',
                  background: 'rgba(255,149,0,0.13)',
                  border: '1px solid rgba(255,149,0,0.30)',
                  borderRadius: 6, padding: '1px 6px',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  flexShrink: 0,
                }}>🧪 ТЕСТ</span>
              )}
            </div>
            {/* Уровень/XP — frosted-glass чип: читается над любым фоном (в т.ч.
                ярким залом на верхней границе экрана) и выглядит современно. */}
            <span
              data-testid="home-character-level"
              style={{
                display: 'inline-flex', alignItems: 'center', marginTop: 4,
                fontSize: 11.5, fontWeight: 600, color: '#2A3B4D',
                background: isClub ? 'rgba(232,255,249,0.52)' : 'rgba(255,255,255,0.52)',
                backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.72)',
                borderRadius: 9, padding: '2.5px 9px',
                boxShadow: '0 1px 5px rgba(26,58,92,0.12)',
                letterSpacing: '0.01em', whiteSpace: 'nowrap',
              }}
            >
              {isDemo ? 'Данных пока нет' : !gamificationOn ? 'Программа тренировок' : `${isClub ? 'Клуб · ' : ''}Уровень ${currentLevel} · ${currentXp} XP`}
            </span>
          </div>
          <button
            data-testid="home-profile-btn"
            onClick={() => { impactHaptic('light'); navigate('/profile') }}
            aria-label="Настройки"
            style={{
              width: 36, height: 36, borderRadius: 12, padding: 0,
              // Плотная матовая подложка + тень — кнопка читается над ярким
              // фоном зала на верхней границе экрана.
              background: 'rgba(255,255,255,0.82)',
              border: isClub ? '1px solid rgba(0,184,148,0.30)' : '1px solid rgba(74,108,247,0.26)',
              boxShadow: '0 1px 5px rgba(26,58,92,0.16)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
              // В демо-режиме настройки тоже доступны — для тестовых сценариев.
            }}
          >
            <Settings size={16} color={isClub ? '#00B894' : '#4A6CF7'} />
          </button>
          <button
            data-testid="home-notifications-bell"
            onClick={() => { if (!isDemo) { impactHaptic('light'); setShowNotifs(v => !v) } }}
            disabled={isDemo}
            aria-label="Уведомления"
            style={{
              width: 36, height: 36, borderRadius: 12, padding: 0,
              // Плотная матовая подложка + тень — читается над ярким фоном зала.
              background: showNotifs && !isDemo
                ? (isClub ? 'rgba(0,184,148,0.22)' : 'rgba(74,108,247,0.22)')
                : 'rgba(255,255,255,0.82)',
              border: showNotifs && !isDemo
                ? (isClub ? '1px solid rgba(0,184,148,0.40)' : '1px solid rgba(74,108,247,0.38)')
                : (isClub ? '1px solid rgba(0,184,148,0.30)' : '1px solid rgba(74,108,247,0.26)'),
              boxShadow: '0 1px 5px rgba(26,58,92,0.16)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', flexShrink: 0,
              cursor: isDemo ? 'default' : 'pointer',
              opacity: isDemo ? 0.35 : 1,
              pointerEvents: isDemo ? 'none' : undefined,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <Bell size={16} color={isClub ? '#00B894' : '#4A6CF7'} />
            {!isDemo && !showNotifs && unreadNotifsCount > 0 && (
              <span data-testid="home-notifications-dot" style={{
                position: 'absolute', top: 7, right: 7,
                width: 6, height: 6, borderRadius: '50%',
                background: '#FF3B30', border: '1.5px solid white',
              }} />
            )}
          </button>
        </div>

        {/* ── Test-week banner (expandable, day X / 7) ── */}
        {isTestWeek && !isDemo && <TestWeekBanner testWeekDay={testWeekDay} />}

        {/* ── Куратор назначен, ждём старта тест-недели (AWAITING_START) ── */}
        {status === 'AWAITING_START' && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(74,108,247,0.12), rgba(162,155,254,0.10))',
            border: '1.5px solid rgba(74,108,247,0.25)', borderRadius: 18, padding: '14px 16px', marginBottom: 14,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 18 }}>🎉</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#1A3A5C' }}>Куратор назначен</span>
            </div>
            <p style={{ fontSize: 13.5, color: '#4A5A6B', margin: 0, lineHeight: 1.55 }}>
              {formatStartDate((user as { flowStartDate?: string })?.flowStartDate)
                ? <>Тестовая неделя стартует <b style={{ color: '#4A6CF7' }}>{formatStartDate((user as { flowStartDate?: string })?.flowStartDate)}</b>. До старта знакомься с приложением — придёт уведомление о начале.</>
                : <>Тестовая неделя скоро стартует. До старта знакомься с приложением — придёт уведомление о начале.</>}
            </p>
          </div>
        )}

        {/* ── 2. Character / Tamagotchi card (увеличенный) ── */}
        <div
          className={characterGray ? 'character-card-gray' : undefined}
          data-testid={characterGray ? 'character-card-gray' : undefined}
          style={{
            // Прозрачная карточка: фон зала теперь полноэкранный слой за ней,
            // персонаж стоит прямо в зале. Бордер/тень/фон убраны.
            borderRadius: 28,
            padding: '14px 14px 18px',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          {/* #545 (три тарифа): геймификации на тарифе нет, персонаж остаётся —
              размытый, поверх плашка про доступ к полной программе. Размывается
              всё содержимое карточки (магазин, монеты, уровень), плашка — нет. */}
          {characterGray && (
            <>
              <style>{`.character-card-gray > *:not(.character-gray-plaque) { filter: blur(7px); pointer-events: none; user-select: none; }`}</style>
              <div className="character-gray-plaque" style={{ position: 'absolute', inset: 0, zIndex: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
                <div style={{ width: '100%', maxWidth: 300, borderRadius: 20, padding: '18px 16px', textAlign: 'center', background: 'rgba(255,255,255,0.9)', boxShadow: '0 10px 26px rgba(26,58,92,0.16)', fontFamily: F }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1A3A5C', lineHeight: 1.3 }}>{lockText('character').title}</div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#6B7A90', lineHeight: 1.4 }}>{lockText('character').subtitle}</div>
                  <button
                    type="button"
                    data-testid="character-gray-cta"
                    onClick={() => { impactHaptic('medium'); openLink(SALES_URL) }}
                    style={{ marginTop: 12, width: '100%', height: 42, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)', color: 'white', fontSize: 13, fontWeight: 700, fontFamily: F }}
                  >
                    🔒 {LOCK_CTA}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Shop button — top-left (hidden in demo) */}
          {!isDemo && (
          <button
            onClick={e => { e.stopPropagation(); impactHaptic('light'); navigate('/shop') }}
            style={{ position: 'absolute', top: 14, left: 14, display: 'inline-flex', alignItems: 'center', gap: 4, height: 28, borderRadius: 14, padding: '0 10px', background: 'rgba(255,255,255,0.62)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(255,149,0,0.22)', fontSize: 12, fontWeight: 700, color: '#B85C00', boxShadow: '0 2px 8px rgba(255,149,0,0.12)', zIndex: 2, cursor: 'pointer' }}
          >
            <ShoppingBag size={13} color="#FF9500" /> Магазин
          </button>
          )}

          {/* TuCoin balance pill — top-right. Tap → opens info sheet. */}
          {!isDemo && (
            <button
              data-testid="home-tucoin-balance"
              onClick={(e) => { e.stopPropagation(); impactHaptic('light'); setShowTuCoinSheet(true) }}
              style={{
                position: 'absolute', top: 14, right: 14,
                height: 30, borderRadius: 16, padding: '0 12px 0 4px',
                // Frosted-glass: плотная матовая подложка + blur, чтобы чип не
                // сливался с фоном зала, с сохранением золотого акцента.
                background: 'linear-gradient(135deg, rgba(255,247,225,0.62) 0%, rgba(255,238,214,0.58) 100%)',
                backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,149,0,0.48)',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 700, color: '#B85C00',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(255,149,0,0.20), 0 1px 4px rgba(26,58,92,0.12)',
                zIndex: 2,
                fontFamily: F,
              }}
            >
              <TuCoinIcon size={24} />
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', opacity: 0.8 }}>
                TuCoin
              </span>
              <span style={{ fontSize: 14, fontWeight: 900 }}>
                {tucoin.toLocaleString('ru-RU')}
              </span>
            </button>
          )}

          {/* Spotlight glow — увеличен, чтобы охватывать более крупного персонажа */}
          <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 280, height: 240, background: isClub ? 'radial-gradient(ellipse,rgba(255,255,255,0.90) 0%,rgba(200,255,230,0.45) 50%,transparent 75%)' : 'radial-gradient(ellipse,rgba(255,255,255,0.90) 0%,rgba(200,220,255,0.45) 50%,transparent 75%)', filter: 'blur(22px)', pointerEvents: 'none', zIndex: 0 }} />

          {/* Hearts row — жизни сверху над персонажем (3 индивидуальных сердца).
              В TEST_WEEK / demo показываем пустые контурные сердечки.
              Клик по сердечкам → объяснение жизней/заморозки. */}
          <div
            data-testid="home-hearts-row"
            role="button"
            tabIndex={isDemo ? -1 : 0}
            onClick={(e) => { e.stopPropagation(); if (isDemo) return; impactHaptic('light'); setShowLivesSheet(true) }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isDemo) { impactHaptic('light'); setShowLivesSheet(true) } } }}
            aria-label="Жизни и заморозки"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 5,
              position: 'absolute',
              top: 14, left: '50%', transform: 'translateX(-50%)',
              height: 32,
              padding: '0 8px',
              zIndex: 2,
              cursor: isDemo ? 'default' : 'pointer',
              fontFamily: F,
            }}
          >
            {[1, 2, 3].map((i) => {
              const active = !isDemo && !isTestWeek && lives >= i
              // Пульсация всех 3 сердец (включая пустые) когда хоть одна потеряна.
              // На 1 жизни — критический режим со свечением.
              // На 0 жизней — самая агрессивная пульсация (все пустые).
              const shouldPulse = !isDemo && !isTestWeek && lives < 3
              const animClass = !shouldPulse
                ? undefined
                : lives <= 1
                  ? 'heart-pulse-critical'
                  : 'heart-pulse-warn'
              return (
                <Heart
                  key={i}
                  size={14}
                  color={active ? '#FF3B30' : 'rgba(255,59,48,0.45)'}
                  fill={active ? '#FF3B30' : 'transparent'}
                  strokeWidth={2.2}
                  className={animClass}
                  style={{
                    filter: active
                      ? 'drop-shadow(0 2px 6px rgba(255,59,48,0.35))'
                      : shouldPulse
                        ? 'drop-shadow(0 0 4px rgba(255,59,48,0.55))'
                        : 'none',
                  }}
                />
              )
            })}
            <style>{`
              @keyframes heart-pulse-warn {
                0%, 100% { transform: scale(1); opacity: 1; }
                50%      { transform: scale(1.22); opacity: 0.92; }
              }
              @keyframes heart-pulse-critical {
                0%, 100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(255,59,48,0)); }
                50%      { transform: scale(1.38); filter: drop-shadow(0 0 10px rgba(255,59,48,0.95)); }
              }
              .heart-pulse-warn { animation: heart-pulse-warn 1.4s ease-in-out infinite; transform-origin: center; }
              .heart-pulse-critical { animation: heart-pulse-critical 0.7s ease-in-out infinite; transform-origin: center; }
            `}</style>
            {/* Баланс заморозок ❄️ — всегда видим в demo/active. Кликабельный → FreezeInfoSheet.
                Active=есть готовая, серый=использована, оранжевый=сейчас активна (снежинка крутится+пульсирует). */}
            {!isDemo && !isTestWeek && (() => {
              const freezeAvail = !!(streakStatus as any)?.freezeAvailable
              const freezeAct = !!streakStatus?.freezeActive
              // Плотная матовая подложка: чип должен читаться над фоном зала на
              // главном (раньше при ~14% прозрачности снежинка почти исчезала).
              const bg = freezeAct
                ? 'rgba(255,243,224,0.94)'
                : freezeAvail
                  ? 'rgba(231,244,255,0.94)'
                  : 'rgba(244,247,250,0.94)'
              const border = freezeAct
                ? 'rgba(255,149,0,0.55)'
                : freezeAvail
                  ? 'rgba(74,159,229,0.50)'
                  : 'rgba(120,135,150,0.38)'
              const iconColor = freezeAct ? '#F57C00' : freezeAvail ? '#1379D6' : '#5B6B7B'
              const textColor = freezeAct ? '#B45309' : freezeAvail ? '#1F4F94' : '#5B6B7B'
              const count = freezeAct ? '●' : freezeAvail ? 1 : 0
              return (
                <button
                  data-testid="home-freeze-count"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); impactHaptic('light'); setShowFreezeSheet(true) }}
                  title={freezeAct ? 'Заморозка активна сейчас — тап для деталей' : freezeAvail ? '1 заморозка доступна — тап чтобы узнать как работает' : 'Заморозок не осталось — можно купить в магазине'}
                  className={freezeAct ? 'freeze-chip-active' : undefined}
                  style={{
                    marginLeft: 4,
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    padding: '2px 6px 2px 4px',
                    borderRadius: 8,
                    background: bg,
                    border: `1px solid ${border}`,
                    color: textColor,
                    fontSize: 11, fontWeight: 800, lineHeight: 1,
                    cursor: 'pointer',
                    fontFamily: F,
                    boxShadow: freezeAct ? '0 0 0 3px rgba(255,149,0,0.18), 0 1px 4px rgba(26,58,92,0.14)' : '0 1px 4px rgba(26,58,92,0.14)',
                  }}>
                  <Snowflake size={11} color={iconColor} />
                  {count}
                  <style>{`
                    @keyframes home-freeze-pulse {
                      0%, 100% { box-shadow: 0 0 0 2px rgba(255,149,0,0.20); transform: scale(1); }
                      50%      { box-shadow: 0 0 0 6px rgba(255,149,0,0.32); transform: scale(1.06); }
                    }
                    @keyframes home-freeze-spin {
                      from { transform: rotate(0deg); }
                      to   { transform: rotate(360deg); }
                    }
                    .freeze-chip-active { animation: home-freeze-pulse 1.6s ease-in-out infinite; }
                    .freeze-chip-active svg { animation: home-freeze-spin 5s linear infinite; transform-origin: center; }
                  `}</style>
                </button>
              )
            })()}
            {isTestWeek && !isDemo && (
              <span style={{
                marginLeft: 4,
                fontSize: 10, fontWeight: 800, color: '#FF9500',
                background: 'rgba(255,149,0,0.13)',
                border: '1px solid rgba(255,149,0,0.30)',
                borderRadius: 6, padding: '2px 6px',
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                Тест
              </span>
            )}
          </div>

          {/* Mascot — центрирован, единственный элемент на этой строке */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
              marginTop: 36,
            }}
          >
            <img
              src={getCharacterImage(characterLevel, (user as { gender?: string })?.gender as never) ?? personaImg}
              alt="Персонаж"
              style={{ width: 420, height: 420, objectFit: 'contain' }}
            />
            <div
              style={{
                position: 'absolute', bottom: 4, left: '50%',
                transform: 'translateX(-50%)',
                width: 160, height: 20,
                background: 'radial-gradient(ellipse at center, rgba(26,58,92,0.18) 0%, transparent 70%)',
                borderRadius: '50%', filter: 'blur(6px)',
                pointerEvents: 'none',
              }}
            />
          </div>

          {/* #302: подсказка дня — между персонажем и чипом серии, ровно там,
              где её нарисовал заказчик. Читается мельком, поэтому без рамки и
              кнопок: просто строка на фоне зала. */}
          <div
            data-testid="home-daily-tip"
            style={{
              position: 'relative', zIndex: 1,
              margin: '2px auto 0', maxWidth: 340,
              padding: '8px 14px', borderRadius: 14,
              background: 'rgba(255,255,255,0.72)',
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.6)',
              boxShadow: '0 6px 18px rgba(26,58,92,0.10)',
              display: 'flex', alignItems: 'flex-start', gap: 8,
              fontFamily: F,
            }}
          >
            <span aria-hidden style={{ fontSize: 15, lineHeight: '18px' }}>{dailyTip.icon}</span>
            <span style={{ fontSize: 12.5, lineHeight: '18px', color: '#1C2D41', fontWeight: 600 }}>
              {dailyTip.text}
            </span>
          </div>

          {/* Streak chip — clickable, opens full history calendar with fire intensity */}
          {!isDemo && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6, position: 'relative', zIndex: 1 }}>
              <button
                data-testid="home-streak-chip"
                onClick={(e) => { e.stopPropagation(); impactHaptic('light'); setShowStreakHistory(true) }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  height: 38, borderRadius: 20, padding: '0 16px 0 6px',
                  background: streakDays >= 21
                    ? 'linear-gradient(135deg,#FF3B30,#C70021)'
                    : streakDays >= 7
                      ? 'linear-gradient(135deg,#FF9500,#FF6B00)'
                      : 'linear-gradient(135deg,#FFD60A,#FF9500)',
                  border: 'none',
                  color: 'white',
                  fontFamily: F,
                  cursor: 'pointer',
                  boxShadow: streakDays >= 21
                    ? '0 6px 22px rgba(255,59,48,0.45), 0 0 0 1.5px rgba(255,214,10,0.45) inset'
                    : '0 6px 18px rgba(255,107,0,0.40), 0 0 0 1.5px rgba(255,255,255,0.55) inset',
                }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.30)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Flame size={18} color="white" fill="rgba(255,255,255,0.85)" />
                </span>
                <span style={{ fontSize: 16, fontWeight: 900, letterSpacing: 0.2 }}>
                  {streakDays}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.92, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  {streakDays === 1 ? 'день' : streakDays >= 2 && streakDays <= 4 ? 'дня' : 'дней'} в режиме
                </span>
              </button>
            </div>
          )}

          {/* Level progress — compact, neutral card, clickable → LevelsProgressionSheet */}
          <button
            data-testid="home-level-progress"
            onClick={(e) => { e.stopPropagation(); if (isDemo) return; impactHaptic('light'); setShowLevelsSheet(true) }}
            disabled={isDemo}
            style={{
              position: 'relative', zIndex: 1,
              width: '100%', textAlign: 'left',
              borderRadius: 14, padding: '8px 12px 9px',
              marginTop: 10,
              background: 'rgba(255,255,255,0.78)',
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(26,58,92,0.10)',
              boxShadow: '0 4px 12px rgba(26,58,92,0.06)',
              color: '#1C2D41', fontFamily: F,
              cursor: isDemo ? 'default' : 'pointer',
              opacity: isDemo ? 0.6 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Level circle */}
              <span style={{
                width: 28, height: 28, borderRadius: '50%',
                background: isClub
                  ? 'linear-gradient(135deg,#00B894,#009C7C)'
                  : 'linear-gradient(135deg,#6C5CE7,#7C3AED)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 900, color: 'white',
                boxShadow: isClub ? '0 3px 8px rgba(0,184,148,0.30)' : '0 3px 8px rgba(108,92,231,0.30)',
                flexShrink: 0,
              }}>
                {isDemo ? '—' : currentLevel}
              </span>

              {/* Progress bar + bottom label */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  height: 7, borderRadius: 5,
                  background: 'rgba(26,58,92,0.08)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.max(2, Math.min(100, isDemo ? 0 : xpPercent))}%`,
                    height: '100%',
                    background: isClub
                      ? 'linear-gradient(90deg, #00B894 0%, #55EFC4 100%)'
                      : 'linear-gradient(90deg, #6C5CE7 0%, #A78BFA 100%)',
                    borderRadius: 5,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ fontSize: 10, color: '#6B7B8D', marginTop: 3, display: 'flex', justifyContent: 'space-between', fontWeight: 700, letterSpacing: 0.2 }}>
                  <span>
                    {isDemo ? 'Заполни анкету' : `${currentXp.toLocaleString('ru-RU')} XP`}
                  </span>
                  {!isDemo && (
                    <span>
                      до Ур. {currentLevel + 1}
                    </span>
                  )}
                </div>
              </div>

              <ChevronRight size={16} color="#9AA5B4" style={{ flexShrink: 0 }} />
            </div>
          </button>
        </div>

        {/* ── FatSecret connect — only bright card in demo mode (prototype).
            position:relative + z-index above Layout's peek-mode click catcher (z-index 150). ── */}
        {isDemo && (
          <div style={{ marginTop: 10, position: 'relative', zIndex: 160 }}>
            <div style={{
              borderRadius: 20, padding: 16,
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              border: '2px solid rgba(76,175,80,0.40)',
              boxShadow: '0 8px 32px rgba(76,175,80,0.22), 0 2px 8px rgba(76,175,80,0.12)',
              display: 'flex', alignItems: 'center', gap: 14,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', left: -24, top: -24, width: 110, height: 110, borderRadius: '50%', background: 'rgba(76,175,80,0.14)', filter: 'blur(30px)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', right: -24, bottom: -24, width: 90, height: 90, borderRadius: '50%', background: 'rgba(76,175,80,0.08)', filter: 'blur(24px)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', flexShrink: 0, zIndex: 1 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 13,
                  background: 'linear-gradient(135deg, #66BB6A 0%, #2E7D32 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FatSecretIcon size={30} color="white" />
                </div>
                <span style={{ position: 'absolute', bottom: -2, right: -2, width: 14, height: 14, borderRadius: '50%', background: '#4CAF50', border: '2.5px solid white', boxShadow: '0 0 0 3px rgba(76,175,80,0.22)' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#1C2D41' }}>FatSecret</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#4CAF50', background: 'rgba(76,175,80,0.12)', borderRadius: 6, padding: '2px 8px', border: '1px solid rgba(76,175,80,0.28)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {fatSecretConnected ? 'Подключено' : 'Доступно сейчас'}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: '#6B7B8D', margin: 0, lineHeight: 1.45 }}>
                  {fatSecretConnected
                    ? (fatSecretStatus?.lastSyncAt
                        ? `Авто-обновление каждый час, итог дня — в 23:59 · последнее: ${new Date(fatSecretStatus.lastSyncAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                        : 'Дневник питания подключён — авто-обновление каждый час, итог дня в 23:59')
                    : 'Подключи дневник питания — считай калории, БЖУ и синхронизируй с профилем'}
                </p>
              </div>
              {fatSecretConnected ? (
                <button
                  onClick={() => { impactHaptic('medium'); syncFatSecretMutation.mutate() }}
                  disabled={syncFatSecretMutation.isPending}
                  title="Синхронизировать"
                  style={{
                    height: 48, width: 48, borderRadius: 14, flexShrink: 0,
                    background: 'rgba(76,175,80,0.12)',
                    border: '1px solid rgba(76,175,80,0.32)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', zIndex: 1,
                    cursor: syncFatSecretMutation.isPending ? 'wait' : 'pointer',
                    padding: 0,
                  }}
                >
                  <RefreshCw
                    size={20}
                    color="#2E7D32"
                    style={{
                      animation: syncFatSecretMutation.isPending ? 'fs-spin 0.9s linear infinite' : undefined,
                    }}
                  />
                  <style>{'@keyframes fs-spin { to { transform: rotate(360deg); } }'}</style>
                </button>
              ) : (
                <button
                  data-testid="fatsecret-connect-button-home"
                  onClick={() => { impactHaptic('medium'); setFsSheet({ open: true, mode: 'connect' }) }}
                  disabled={connectFatSecretMutation.isPending}
                  style={{
                    height: 48, borderRadius: 14, padding: '0 18px', border: 'none',
                    background: 'linear-gradient(135deg,#66BB6A,#2E7D32)',
                    color: 'white', fontSize: 13, fontWeight: 700,
                    cursor: connectFatSecretMutation.isPending ? 'wait' : 'pointer',
                    flexShrink: 0, boxShadow: '0 4px 16px rgba(76,175,80,0.42)',
                    whiteSpace: 'nowrap', position: 'relative', zIndex: 1,
                    opacity: connectFatSecretMutation.isPending ? 0.7 : 1,
                  }}
                >
                  {connectFatSecretMutation.isPending ? '...' : 'Подключить'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* In demo mode: dim everything below the character card per prototype DemoOverlay.
            The absolute catcher inside has pointer-events:auto and shows the "Ожидай старт"
            tooltip — sits inside the dimmed area only, so the FatSecret card above stays clickable. */}
        <div style={isDemo ? { opacity: 0.18, filter: 'grayscale(0.35)', pointerEvents: 'none', transition: 'opacity 0.3s ease', position: 'relative' } : undefined}>
        {isDemo && (
          <div
            onClick={handlePeekTap}
            style={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'auto', cursor: 'default' }}
          />
        )}

        {/* ── 6. Analytics carousel ── */}
        <div style={{ marginTop: 10 }}>
          <div
            data-testid="home-analytics-card"
            onClick={() => navigate('/analytics')}
            style={{ borderRadius: 22, overflow: 'hidden', position: 'relative', boxShadow: '0 6px 24px rgba(26,58,92,0.16)', cursor: 'pointer' }}
          >
            <div
              ref={analyticsRef}
              style={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onScroll={e => {
                const el = e.currentTarget
                setAnalyticsIdx(Math.round(el.scrollLeft / el.offsetWidth))
              }}
            >
              {/* Card 1 — Weight */}
              <div data-testid="home-analytics-card-weight" style={{ minWidth: '100%', scrollSnapAlign: 'start', background: isClub ? 'linear-gradient(135deg,#007852 0%,#00B894 100%)' : 'linear-gradient(135deg,#1A3A5C 0%,#1E4080 100%)', padding: '16px 18px 14px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10, position: 'relative', zIndex: 1 }}>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', margin: '0 0 3px', letterSpacing: 1, textTransform: 'uppercase' }}>Прогресс · Вес</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                      <span data-testid="home-analytics-value-weight" style={{ fontSize: 28, fontWeight: 800, color: 'white', lineHeight: 1 }}>{weightLast != null ? weightLast.toFixed(1) : '—'}</span>
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>кг</span>
                      {weightWeeklyPct != null && Number.isFinite(weightWeeklyPct) && (
                        <span
                          data-testid="home-analytics-weight-weekly-pct"
                          title="Изменение веса за последние 7 дней"
                          style={{
                            marginLeft: 4,
                            fontSize: 11, fontWeight: 800, lineHeight: 1,
                            padding: '3px 7px', borderRadius: 7,
                            // #562: тон — по цели клиента (на наборе массы рост зелёный).
                            background: weightWeeklyTone === 'good' ? 'rgba(52,199,89,0.22)' : weightWeeklyTone === 'bad' ? 'rgba(255,149,0,0.22)' : 'rgba(255,255,255,0.18)',
                            color: weightWeeklyTone === 'good' ? '#34C759' : weightWeeklyTone === 'bad' ? '#FFB454' : 'rgba(255,255,255,0.85)',
                          }}
                        >
                          {weightWeeklyPct > 0 ? '+' : ''}{weightWeeklyPct.toFixed(1)}% / нед
                        </span>
                      )}
                    </div>
                    {weightSmoothLast != null && (
                      <p
                        data-testid="home-analytics-weight-smooth"
                        title="Вес с поправкой на колебания воды/еды (EMA)"
                        style={{ fontSize: 11, color: 'rgba(255,255,255,0.62)', margin: '5px 0 0', fontWeight: 600 }}
                      >
                        С поправкой: {weightSmoothLast.toFixed(1)} кг
                      </p>
                    )}
                    {weightPoints.length > 1 && (
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '3px 0 0' }}>за {weightPoints.length} {weightPoints.length === 1 ? 'запись' : weightPoints.length < 5 ? 'записи' : 'записей'}</p>
                    )}
                  </div>
                </div>
                {weightPoints.length >= 2 ? (() => {
                  const vals = weightPoints.map(p => p.weight)
                  const min = Math.min(...vals)
                  const max = Math.max(...vals)
                  const range = max - min || 1
                  const stepX = vals.length > 1 ? 280 / (vals.length - 1) : 0
                  const points = vals.map((v, i) => `${i * stepX} ${40 - ((v - min) / range) * 32}`).join(' L ')
                  return (
                    <svg width="100%" height={48} viewBox="0 0 280 48" preserveAspectRatio="none" style={{ display: 'block', position: 'relative', zIndex: 1 }}>
                      <defs>
                        <linearGradient id="wG" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#34C759" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#34C759" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <path d={`M ${points}`} stroke="#34C759" strokeWidth={2} fill="none" strokeLinecap="round" />
                      <path d={`M ${points} L 280 48 L 0 48 Z`} fill="url(#wG)" />
                      {vals.map((v, i) => (
                        <circle key={i} cx={i * stepX} cy={40 - ((v - min) / range) * 32} r={3} fill="white" />
                      ))}
                    </svg>
                  )
                })() : (
                  <div style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                    {weightPoints.length === 1 ? 'Запиши ещё вес для графика' : 'Нет данных'}
                  </div>
                )}
              </div>

              {/* Card 2 — Steps */}
              <div data-testid="home-analytics-card-steps" style={{ minWidth: '100%', scrollSnapAlign: 'start', background: 'linear-gradient(135deg,#0D3B6E 0%,#1E5A9C 100%)', padding: '16px 18px 14px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, position: 'relative', zIndex: 1 }}>
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', margin: '0 0 3px', letterSpacing: 1, textTransform: 'uppercase' }}>Активность · Шаги</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span data-testid="home-analytics-value-steps" style={{ fontSize: 28, fontWeight: 800, color: 'white', lineHeight: 1 }}>
                        {stepsAvg > 0 ? `${(stepsAvg / 1000).toFixed(1)}k` : '—'}
                      </span>
                    </div>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '3px 0 0' }}>ср. за неделю</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 48, position: 'relative', zIndex: 1 }}>
                  {stepsSeries.map((v, i) => {
                    const max = Math.max(10000, ...stepsSeries.map(s => s ?? 0))
                    const h = v != null ? Math.round((v / max) * 40) : 4
                    const today = i === stepsSeries.length - 1
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <div style={{ width: '100%', height: h, borderRadius: 4, background: today ? '#74B9FF' : 'rgba(116,185,255,0.25)', boxShadow: today ? '0 0 8px rgba(116,185,255,0.5)' : 'none' }} />
                        <span style={{ fontSize: 8, color: today ? '#74B9FF' : 'rgba(255,255,255,0.28)' }}>{['Пн','Вт','Ср','Чт','Пт','Сб','Вс'][i]}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Card 3 — Sleep */}
              <div data-testid="home-analytics-card-sleep" style={{ minWidth: '100%', scrollSnapAlign: 'start', background: 'linear-gradient(135deg,#2D1B69 0%,#4A3A9E 100%)', padding: '16px 18px 14px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', margin: '0 0 3px', letterSpacing: 1, textTransform: 'uppercase' }}>Восстановление · Сон</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                      <span data-testid="home-analytics-value-sleep" style={{ fontSize: 28, fontWeight: 800, color: 'white', lineHeight: 1 }}>
                        {sleepLogged > 0 ? sleepGoodN : '—'}
                      </span>
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                        {sleepLogged > 0 ? `/ ${sleepLogged} ноч.` : 'нет данных'}
                      </span>
                    </div>
                    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '0 0 10px' }}>
                      {sleepLogged > 0 ? 'выспался · за 7 дней' : 'отметь сон в дневнике'}
                    </p>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 30 }}>
                      {sleepQSeries.map((q, i) => (
                        <div key={i} style={{ flex: 1, height: q != null ? 28 : 6, borderRadius: 3, background: q != null ? SLEEP_Q_COLOR[q] : 'rgba(255,255,255,0.14)' }} />
                      ))}
                    </div>
                  </div>
                  <svg width={68} height={68} viewBox="0 0 68 68" style={{ flexShrink: 0, marginTop: 4 }}>
                    <circle cx={34} cy={34} r={26} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={7} />
                    <circle cx={34} cy={34} r={26} fill="none" stroke="#A29BFE" strokeWidth={7} strokeLinecap="round"
                      strokeDasharray={`${(sleepGoodPct / 100) * 163.4} 163.4`} strokeDashoffset={40.85} />
                    <text x={34} y={39} textAnchor="middle" fontSize={13} fontWeight={700} fill="white">
                      {sleepLogged > 0 ? `${sleepGoodPct}%` : '—'}
                    </text>
                  </svg>
                </div>
              </div>

              {/* Card 4 — Workouts */}
              <div style={{ minWidth: '100%', scrollSnapAlign: 'start', background: 'linear-gradient(135deg,#1A3A5C 0%,#2D5BE3 100%)', padding: '16px 18px 14px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle,rgba(255,255,255,0.04) 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', margin: '0 0 3px', letterSpacing: 1, textTransform: 'uppercase' }}>Тренировки · 2 нед.</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: 'white', lineHeight: 1 }}>{workoutsCount}</span>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{workoutsCount === 1 ? 'тренировка' : workoutsCount < 5 ? 'тренировки' : 'тренировок'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {workoutsHeatmap.map((done, i) => (
                      <div key={i} style={{ flex: 1, height: 26, borderRadius: 5, background: done ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.07)', border: done ? '1px solid rgba(255,255,255,0.25)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {done && <span style={{ fontSize: 9, color: 'white' }}>✓</span>}
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.32)', margin: '5px 0 0', textAlign: 'center' }}>последние 14 дней</p>
                </div>
              </div>
            </div>

            {/* Dots strip */}
            <div style={{ background: 'rgba(8,18,72,0.88)', backdropFilter: 'blur(14px)', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                {[0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    onClick={e => { e.stopPropagation(); analyticsRef.current?.scrollTo({ left: i * (analyticsRef.current.offsetWidth), behavior: 'smooth' }); setAnalyticsIdx(i) }}
                    style={{ width: analyticsIdx === i ? 18 : 5, height: 5, borderRadius: 3, background: analyticsIdx === i ? 'white' : 'rgba(255,255,255,0.25)', transition: 'width 0.25s ease', cursor: 'pointer' }}
                  />
                ))}
              </div>
              <span data-testid="home-analytics-full-link" style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 3 }}>
                Полная аналитика <ChevronRight size={12} color="rgba(255,255,255,0.5)" />
              </span>
            </div>
          </div>
        </div>

        {/* ── 7. League widget ── */}
        {features.has('leaderboard') && myLeague && (
          <div style={{ marginTop: 10 }}>
            <MyLeagueWidget
              leagueIcon={<Award size={22} color="#A8A8A8" />}
              leagueName={myLeague.leagueName || 'Серебряная лига'}
              nextLeagueName={myLeague.nextLeagueName || 'Золотой'}
              xpToNext={myLeague.xpToNext || 340}
              collapsible={true}
              defaultExpanded={false}
              neighbors={myLeague.neighbors || []}
              inDemotionZone={myLeague.inDemotionZone || false}
              onPress={() => navigate('/leaderboard')}
            />
          </div>
        )}

        {/* ── 8. Active Challenge ── */}
        {features.has('challenges') && activeChallenge && (
          <div style={{ marginTop: 10 }}>
            <GlassCard variant="accent">
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'white', margin: 0 }}>
                {activeChallenge.name || 'Челлендж'}
              </h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', margin: '4px 0 12px', display: 'flex', alignItems: 'center', gap: 4 }}>
                {activeChallenge.durationDays} дней <TuCoinIcon size={14} />
              </p>
              <ProgressBar
                percent={challengeProgress}
                gradient="rgba(255,255,255,0.9)"
                trackColor="rgba(255,255,255,0.3)"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
                  День {activeChallenge.daysCompleted} из {activeChallenge.durationDays}
                </span>
                <button
                  onClick={() => navigate('/leaderboard/challenges')}
                  style={{ background: 'transparent', border: 'none', color: 'white', fontSize: 15, cursor: 'pointer' }}
                >
                  Подробнее &rarr;
                </button>
              </div>
            </GlassCard>
          </div>
        )}

        {/* ── 9. Curator + Обратная связь (2-column) ──
            #534: в Клубе куратора нет, а «Задать вопрос» вела в ту же связь с
            сопровождением — весь блок с главной убран, поддержка остаётся в
            профиле. */}
        {curatorHidden ? null : isClub ? (
          <div style={{ marginTop: 10 }}>
            <div
              onClick={() => { impactHaptic('light'); openLink(FEEDBACK_URL) }}
              style={{
                borderRadius: 18, padding: '16px',
                background: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(36,161,222,0.18)',
                boxShadow: '0 6px 20px rgba(36,161,222,0.08)',
                display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
              }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#24A1DE,#4FC3F7)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(36,161,222,0.4)' }}>
                  <MessageCircle size={26} color="white" />
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 3 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#1C2D41' }}>Задать вопрос</span>
                </div>
                <p style={{ fontSize: 12, color: '#9AA5B4', margin: 0 }}>Оставь обратную связь, жалобу или пожелание — мы решим твой вопрос</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); impactHaptic('light'); openLink(FEEDBACK_URL) }}
                style={{ height: 44, borderRadius: 14, padding: '0 16px', border: 'none', background: 'linear-gradient(135deg,#24A1DE,#4FC3F7)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 16px rgba(36,161,222,0.3)' }}
              >
                Написать
              </button>
            </div>
          </div>
        ) : (
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          {/* Curator — info-only (для связи закрыть мини-апп и открыть Telegram) */}
          <div
            style={{
              flex: '1 1 0', minWidth: 0, borderRadius: 18, padding: '12px 12px 14px',
              background: 'linear-gradient(135deg,rgba(36,161,222,0.08) 0%,rgba(40,182,247,0.04) 100%),rgba(255,255,255,0.78)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(36,161,222,0.22)',
              boxShadow: '0 6px 20px rgba(26,58,92,0.07)',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {curator?.avatarUrl ? (
                <img
                  src={resolveMediaUrl(curator.avatarUrl)}
                  alt=""
                  style={{
                    width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
                    flexShrink: 0, border: '2px solid rgba(36,161,222,0.3)',
                    boxShadow: '0 3px 10px rgba(36,161,222,0.25)',
                  }}
                />
              ) : (
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#24A1DE,#4FC3F7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: 'white', flexShrink: 0,
                  boxShadow: '0 3px 10px rgba(36,161,222,0.35)',
                }}>
                  {curator?.name?.charAt(0) || 'К'}
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <p data-testid="home-curator-name" style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {curator?.name || 'Куратор'}
                </p>
                <p style={{ fontSize: 11, color: '#6B7B8D', margin: 0 }}>Куратор</p>
              </div>
            </div>
            <p style={{
              fontSize: 11, color: '#6B7B8D', lineHeight: 1.4,
              margin: 0,
            }}>
              Напиши в чат — куратор тебе ответит.
            </p>
          </div>

          {/* Обратная связь */}
          <div
            onClick={() => { impactHaptic('light'); openLink(FEEDBACK_URL) }}
            style={{
              flex: '1 1 0', minWidth: 0, borderRadius: 18, padding: '12px 12px 14px',
              background: 'linear-gradient(135deg,rgba(36,161,222,0.09) 0%,rgba(79,195,247,0.05) 100%),rgba(255,255,255,0.78)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(36,161,222,0.22)',
              boxShadow: '0 6px 20px rgba(36,161,222,0.08)',
              display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#24A1DE,#4FC3F7)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(36,161,222,0.35)' }}>
                  <MessageCircle size={18} color="white" />
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', margin: 0, whiteSpace: 'nowrap' }}>Задать вопрос</p>
                <p style={{ fontSize: 11, color: '#6B7B8D', margin: 0 }}>Обратная связь</p>
              </div>
            </div>
            <p style={{ fontSize: 11, color: '#9AA5B4', margin: 0, flex: 1 }}>
              Вопрос, жалоба или пожелание — мы поможем
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); impactHaptic('light'); openLink(FEEDBACK_URL) }}
              style={{ height: 32, borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#24A1DE,#4FC3F7)', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
            >
              <MessageCircle size={12} color="white" /> Написать
            </button>
          </div>
        </div>
        )}

        </div>{/* /demo-dimming wrapper */}

      </div>

      {/* Peek-mode tooltip (R2 / doc 17) */}
      <AnimatePresence>
        {peekTip && (
          <motion.div
            key={peekTip.key}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              left: peekTip.x,
              top: peekTip.y - 44,
              transform: 'translateX(-50%)',
              zIndex: 220,
              background: 'rgba(22,20,60,0.92)',
              color: 'white',
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 14px',
              borderRadius: 14,
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            ⏳ Ожидай старт
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Gamification modals ── */}
      <FreezeActiveModal
        open={showFreezeModal}
        onClose={() => { setShowFreezeModal(false); navigate('/diary') }}
        initialSeconds={streakStatus?.hoursRemaining ? Math.round(streakStatus.hoursRemaining * 3600) : undefined}
        streakDays={streakStatus?.streakDays ?? streakDays}
      />
      <LifeLostModal
        open={showLifeLostModal}
        onClose={() => { setShowLifeLostModal(false); navigate('/diary') }}
        livesRemaining={streakStatus?.lives ?? 2}
      />
      <LastChanceModal
        open={showLastChanceModal}
        onClose={() => setShowLastChanceModal(false)}
      />
      <WeeklyResultsModal
        open={showWeeklyResultsModal}
        onClose={() => setShowWeeklyResultsModal(false)}
      />
      <LevelCapModal
        open={showLevelCapModal}
        onClose={() => setShowLevelCapModal(false)}
        level={currentLevel}
        product={user?.product || undefined}
      />

      {/* TuCoin balance / info popup */}
      <TuCoinInfoSheet
        open={showTuCoinSheet}
        onClose={() => setShowTuCoinSheet(false)}
        balance={tucoin}
      />

      {/* Streak history calendar (fire-intensity per day) */}
      <StreakHistorySheet
        open={showStreakHistory}
        onClose={() => setShowStreakHistory(false)}
        streakDays={streakDays}
        streakRecord={streakRecord}
        lives={lives}
      />

      {/* Levels progression (mini character previews per level + XP requirements) */}
      <LevelsProgressionSheet
        open={showLevelsSheet}
        onClose={() => setShowLevelsSheet(false)}
        currentLevel={currentLevel}
        currentXp={currentXp}
        product={user?.product ?? null}
        gender={(user as { gender?: string })?.gender ?? null}
      />

      {/* Lives / freeze info (tap on hearts) */}
      <LivesInfoSheet
        open={showLivesSheet}
        onClose={() => setShowLivesSheet(false)}
        lives={lives}
        freezeActive={!!streakStatus?.freezeActive}
        freezeAvailable={!!(streakStatus as any)?.freezeAvailable}
        hoursRemaining={streakStatus?.hoursRemaining ?? null}
      />

      {/* Freeze info (tap on ❄️-chip near hearts) */}
      <FreezeInfoSheet
        open={showFreezeSheet}
        onClose={() => setShowFreezeSheet(false)}
        freezeActive={!!streakStatus?.freezeActive}
        freezeAvailable={!!(streakStatus as any)?.freezeAvailable}
        hoursRemaining={streakStatus?.hoursRemaining ?? null}
      />

      {/* FatSecret connect/reconnect sheet (intermediate dialog with SSO guidance) */}
      <FatSecretConnectSheet
        open={fsSheet.open}
        mode={fsSheet.mode}
        onClose={() => setFsSheet((s) => ({ ...s, open: false }))}
        onConnect={() => {
          setFsSheet((s) => ({ ...s, open: false }))
          connectFatSecretMutation.mutate()
        }}
        isPending={connectFatSecretMutation.isPending}
      />

      <NotificationsPanel open={showNotifs} onClose={() => setShowNotifs(false)} />
    </Layout>
  )
}
